import { backendBaseUrl } from '../config';
import { fetchWithTimeout } from '../auth/appAuth';
import type { ApiErrorPayload } from '../../lib/i18nResolve';

/** GET /api/tos/status and POST /api/tos/accept share this body (docs: backend PR "ToS acceptance record + gate"). */
export interface TosStatus {
  required: boolean;
  enforcement_enabled: boolean;
  current_version: string;
  accepted: boolean;
  accepted_version: string | null;
  accepted_at: string | null;
  terms_url: string | null;
}

/** A refused ToS call, carrying the backend's typed ADR-043 payload so the UI can localize it. */
export class TosApiError extends Error {
  constructor(readonly status: number, readonly payload: ApiErrorPayload | null) {
    super(`ToS call refused: HTTP ${status} ${payload?.error ?? ''}`.trim());
    this.name = 'TosApiError';
  }
}

const TIMEOUT_MS = 8000;

async function read(resp: Response): Promise<TosStatus> {
  if (!resp.ok) {
    let payload: ApiErrorPayload | null = null;
    try {
      payload = (await resp.json()) as ApiErrorPayload;
    } catch {
      payload = null; // non-JSON refusal (proxy/5xx): surfaced as an untyped TosApiError, never as success
    }
    throw new TosApiError(resp.status, payload);
  }
  return (await resp.json()) as TosStatus;
}

export async function fetchTosStatus(): Promise<TosStatus> {
  return read(await fetchWithTimeout(`${backendBaseUrl}/api/tos/status`, { credentials: 'include' }, TIMEOUT_MS));
}

export async function acceptTos(version: string): Promise<void> {
  const resp = await fetchWithTimeout(`${backendBaseUrl}/api/tos/accept`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ version, agreed: true }),
  }, TIMEOUT_MS);
  // The response body is deliberately not returned: the caller refetches status (persist-then-render).
  await read(resp);
}
